import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '@/lib/utils';

// Type for lucide-react icon components
type IconComponent = React.ComponentType<{ className?: string }>;

interface MenuItem {
  id: string;
  label: string;
  icon?: IconComponent;
  href?: string;
  children?: MenuItem[];
}

interface NavigationMenuProps {
  items: MenuItem[];
  onItemClick?: (item: MenuItem) => void;
  className?: string;
}

/**
 * NavigationMenu Component
 *
 * A navigation menu with accordion-style behavior where:
 * - All items start collapsed by default
 * - Only one menu item can be expanded at a time
 * - Clicking an expanded item collapses it
 * - Clicking a collapsed item expands it and collapses any other expanded item
 */
export function NavigationMenu({ items, onItemClick, className }: NavigationMenuProps) {
  // Store only one expanded item ID at a time (null means all collapsed)
  const [expandedItemId, setExpandedItemId] = useState<string | null>(null);

  const handleToggle = (itemId: string) => {
    // Accordion behavior: toggle the clicked item
    // If it's already expanded, collapse it; otherwise expand it and collapse others
    setExpandedItemId((prev) => (prev === itemId ? null : itemId));
  };

  const handleItemClick = (item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      // If item has children, toggle expansion
      handleToggle(item.id);
    } else if (onItemClick) {
      // If item has no children, trigger the click callback
      onItemClick(item);
    }
  };

  const renderMenuItem = (item: MenuItem, level: number = 0) => {
    const hasChildren = item.children && item.children.length > 0;
    const isExpanded = expandedItemId === item.id;
    const Icon = item.icon;

    return (
      <div key={item.id} className="w-full">
        <button
          onClick={() => handleItemClick(item)}
          className={cn(
            'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            'text-muted-foreground hover:bg-accent hover:text-accent-foreground',
            level > 0 && 'ml-4',
          )}
          aria-expanded={hasChildren ? isExpanded : undefined}
          aria-controls={hasChildren ? `submenu-${item.id}` : undefined}
        >
          <div className="flex items-center gap-3">
            {Icon && <Icon className={cn('h-5 w-5', level > 0 && 'h-4 w-4')} />}
            <span>{item.label}</span>
          </div>
          {hasChildren && (
            <span className="transition-transform duration-200">
              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </span>
          )}
        </button>

        {/* Render children with smooth transition */}
        {hasChildren && (
          <div
            id={`submenu-${item.id}`}
            className={cn(
              'overflow-hidden transition-all duration-300 ease-in-out',
              isExpanded ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0',
            )}
          >
            <div className="ml-4 mt-1 space-y-1 border-l pl-4">
              {item.children?.map((child) => renderMenuItem(child, level + 1))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <nav className={cn('space-y-1', className)} role="navigation" aria-label="Main navigation">
      {items.map((item) => renderMenuItem(item, 0))}
    </nav>
  );
}
